/*
 * The measures of a mill by its kind and where it stands. A mill on the ground keeps PLAN §5's
 * measures. A mill on a city wall stands on the flat top of a tower or bastion, inside its 1 m
 * parapet (ruling 2026-09-28): the post mill on a 1 m mound, which the woodcut (1544, px 2105-2165
 * x 1230-1320) draws as a foot inside the bastion's rim, and the tower mill as the stone shaft the
 * woodcut (px 3130-3165 x 2130-2190) draws about 1.5 times the wall's height, with sails 0.8 times
 * its body. Either way the lowest sail tip clears the parapet.
 */
export type WindmillKind = 'post' | 'tower';

export type WindmillSetting = 'ground' | 'wall';

export interface MillShaft {
  readonly height: number;
  readonly radiusBottom: number;
  readonly radiusTop: number;
}

export interface MillMound {
  readonly height: number;
  readonly radiusBottom: number;
  readonly radiusTop: number;
}

export interface WindmillForm {
  /** Sail stock length from the axle. */
  readonly sailRadius: number;
  /** Axle height above the mill's base: the ground, or its tower's top. */
  readonly hubHeight: number;
  /** Stock centre plane along the axle: the stocks' back face meets the front of the widest body part. */
  readonly stockX: number;
  /** Earth the post mill's trestle stands on; none on the ground. */
  readonly mound: MillMound | null;
  /** The tower mill's masonry under its cap. */
  readonly shaft: MillShaft;
}

export const POST_BODY = { length: 4, width: 3, height: 5, base: 4 } as const;
export const STOCK_HALF_WIDTH = 0.15;
export const SAIL_RADIUS_MIN = 10;
const WALL_TOWER_MILL_SAILS = 8;
/* 0.5 m under the lowest tip; the axle then sits in the post mill's roof gable, below the ridge. */
const SAIL_GROUND_CLEARANCE = 0.5;
/* The cap's axle sits 1 m over the top of the shaft. */
const AXLE_OVER_SHAFT = 1;
const POST_STOCK_X = POST_BODY.length / 2 + 0.6 + STOCK_HALF_WIDTH;
/* The quarter bars' feet lie 1.9 m out and the cross trees 2.2 m, so the mound's top takes both. */
const WALL_MOUND: MillMound = { height: 1, radiusBottom: 2.8, radiusTop: 2.4 };
const GROUND_SHAFT: MillShaft = { height: 12, radiusBottom: 3, radiusTop: 2 };
/* Inside the 0.5 m parapet of a 3.5 m tower. */
const WALL_SHAFT: MillShaft = { height: 8.5, radiusBottom: 2.3, radiusTop: 2 };

function postForm(mound: MillMound | null): WindmillForm {
  const standing = mound?.height ?? 0;
  return {
    sailRadius: SAIL_RADIUS_MIN,
    hubHeight: standing + SAIL_RADIUS_MIN + SAIL_GROUND_CLEARANCE,
    stockX: POST_STOCK_X,
    mound,
    shaft: GROUND_SHAFT,
  };
}

function towerForm(shaft: MillShaft, sailRadius: number): WindmillForm {
  return {
    sailRadius,
    hubHeight: shaft.height + AXLE_OVER_SHAFT,
    stockX: shaft.radiusBottom + 0.1 + STOCK_HALF_WIDTH,
    mound: null,
    shaft,
  };
}

const FORMS: Readonly<Record<WindmillKind, Readonly<Record<WindmillSetting, WindmillForm>>>> = {
  post: { ground: postForm(null), wall: postForm(WALL_MOUND) },
  tower: {
    ground: towerForm(GROUND_SHAFT, SAIL_RADIUS_MIN),
    wall: towerForm(WALL_SHAFT, WALL_TOWER_MILL_SAILS),
  },
};

export function windmillForm(kind: WindmillKind, setting: WindmillSetting): WindmillForm {
  return FORMS[kind][setting];
}
